/**
 * useAdminDashboard — consolidates every data fetch the refreshed
 * admin dashboard needs into a single
 * `AsyncResource<AdminDashboardData>`.
 *
 * Mirrors the contestant-side `useApplicantDashboard` pattern: one
 * loading boundary, one error boundary, derived counts (submitted /
 * approved / pending titles / pending costumes) computed inside the
 * hook so the rendering component is a pure presentation layer.
 *
 * Approval status, titles, and counts are sourced from the Approval
 * SSOT via `applicationFormClient.listResponses(competitionId)` —
 * replacing the legacy per-applicant `listApplicantYearProfiles`
 * roster whose fields were dropped in the AYP→Participation cutover
 * (the cause of the zeroed KPIs). Inquiries + events are unchanged.
 *
 * Spec: meridian-redesign — PR (m.7) Phase 2 (Admin Dashboard refresh);
 *       approval-status-ssot-unification AC4 (approval status from SSOT)
 * Requirements: 8.1, 8.2, 8.3, 14.1, 18.5
 */

import { useCallback, useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from 'aws-amplify/api';
import { applicationFormClient } from '@/lib/application-form/client';
import type { ApplicationResponseSummary } from '@/lib/application-form/client';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { fetchUpcomingEventsFromApi, limitUpcomingEvents, type CalendarEvent } from '@/data/calendarEvents';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useActiveTenantId } from '@/context/AuthContext';
import type { AsyncResource } from './asyncResource';

const client = generateClient();

export interface AdminDashboardInquiry {
  id: string;
  name: string;
  email: string;
  subject: string;
  createdAt: string;
  status?: string;
}

/** Pipeline counts for the application-funnel chart. */
export interface AdminPipelineCounts {
  draft: number;
  submitted: number;
  approved: number;
  rejected: number;
}

export interface AdminDashboardKpis {
  totalApplications: number;
  submitted: number;
  approved: number;
  pendingTitles: number;
}

export interface AdminDashboardData {
  /** The Approval SSOT review summaries for the active competition. */
  applicants: ApplicationResponseSummary[];
  recentInquiries: AdminDashboardInquiry[];
  upcomingEvents: CalendarEvent[];
  kpis: AdminDashboardKpis;
  pipeline: AdminPipelineCounts;
  competitionYear: number;
}

export function useAdminDashboard(): AsyncResource<AdminDashboardData> {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);

  // Season SSOT: the operative competition year resolves through the
  // active-season seam (season-ssot) rather than a static COMPETITION_YEAR
  // literal. While the season query is loading/unresolved, `operativeYear`
  // is the sanctioned bootstrap fallback the seam itself defines.
  const { operativeYear } = useActiveSeason();
  const activeTenantId = useActiveTenantId();

  const refetch = useCallback(() => {
    setRefetchTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Applicant responses for the active competition — sourced from the
        //    Approval SSOT (`listResponses`), keyed by personId/Participation.
        //    This replaces the legacy per-applicant `listApplicantYearProfiles`
        //    roster, whose title/status/costume fields were dropped in the
        //    AYP→Participation/ApplicationResponse cutover (so the KPIs read 0).
        //    Only applicants who have started an application appear here, so the
        //    director-only login-tracking records are naturally excluded.
        const competitionId = resolveActiveCompetitionId(operativeYear, activeTenantId);
        const summaries = await applicationFormClient.listResponses(competitionId);

        // 2. Recent inquiries.
        let recentInquiries: AdminDashboardInquiry[] = [];
        try {
          const inq = (await client.graphql({
            query: `query {
              listInquiries(limit: 10) {
                items { id name email subject createdAt status }
              }
            }`,
            authMode: 'userPool',
          })) as GraphQLResult<{
            listInquiries: { items: AdminDashboardInquiry[] };
          }>;
          const items = inq.data?.listInquiries?.items ?? [];
          items.sort((a, b) =>
            (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
          );
          recentInquiries = items.slice(0, 4);
        } catch {
          // Soft fail — render dashboard with empty inquiries.
        }

        // 3. Upcoming events.
        const events = await fetchUpcomingEventsFromApi(operativeYear);
        const upcomingEvents = limitUpcomingEvents(events, 3);

        // 4. Derive KPI + pipeline counts.
        const kpis = computeKpis(summaries);
        const pipeline = computePipeline(summaries);

        if (cancelled) return;

        setData({
          applicants: summaries,
          recentInquiries,
          upcomingEvents,
          kpis,
          pipeline,
          competitionYear: operativeYear,
        });
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refetchTick, operativeYear, activeTenantId]);

  return { data, loading, error, refetch };
}

/**
 * Derive the KPI-strip counts from the Approval SSOT review summaries
 * (`listResponses`). Each summary is one applicant's `ApplicationResponse` for
 * the competition, so there is exactly one row per applicant — no per-year
 * filtering is needed (the list is already competition-scoped).
 *
 *   - `totalApplications` — every applicant with a started application.
 *   - `submitted` / `approved` — `ApplicationResponse.submissionStatus`
 *     (the Approval SSOT; approval-status-ssot-unification AC4).
 *   - `pendingTitles` — a `Pending_Title_Intent` (non-empty ranked
 *     `titlePreferences`) with no director-approved title yet.
 */
export function computeKpis(summaries: ApplicationResponseSummary[]): AdminDashboardKpis {
  const submitted = summaries.filter((s) => s.submissionStatus === 'SUBMITTED').length;
  const approved = summaries.filter((s) => s.submissionStatus === 'APPROVED').length;
  const pendingTitles = summaries.filter(
    (s) => (s.titlePreferences?.length ?? 0) > 0 && !s.approvedTitle,
  ).length;
  return {
    totalApplications: summaries.length,
    submitted,
    approved,
    pendingTitles,
  };
}

export function computePipeline(summaries: ApplicationResponseSummary[]): AdminPipelineCounts {
  let draft = 0;
  let submitted = 0;
  let approved = 0;
  let rejected = 0;
  for (const s of summaries) {
    switch (s.submissionStatus) {
      case 'DRAFT':
        draft += 1;
        break;
      case 'SUBMITTED':
        submitted += 1;
        break;
      case 'APPROVED':
        approved += 1;
        break;
      case 'REJECTED':
        rejected += 1;
        break;
      default:
        break;
    }
  }
  return { draft, submitted, approved, rejected };
}
